import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, History, ShieldCheck, CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";
import { LEGAL_QUILL_FORMATS, LEGAL_QUILL_TOOLBAR } from "@/lib/legalFormats";

const QUILL_MODULES = { toolbar: LEGAL_QUILL_TOOLBAR };
const QUILL_FORMATS = LEGAL_QUILL_FORMATS;

/**
 * Admin panel: unified management for the four public legal pages.
 * Route: /app/admin/legal-pages   (mount from App.js requires-admin route)
 */
export default function AdminLegalPagesPage() {
  const [summary, setSummary] = useState(null);
  const [activePage, setActivePage] = useState("sender_agreement");
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState("");
  const [dirty, setDirty] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [changeNotes, setChangeNotes] = useState("");
  const [explicitVersion, setExplicitVersion] = useState("");
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/legal-pages");
      setSummary(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load summary");
    } finally {
      setLoading(false);
    }
  };

  const loadPage = async (page) => {
    setActivePage(page);
    setDraft("");
    setDirty(false);
    setCurrent(null);
    setHistory([]);
    setShowHistory(false);
    try {
      const r = await api.get(`/admin/legal-pages/${page}`);
      setCurrent(r.data);
      setDraft(r.data.content_html || "");
      setDirty(false);
    } catch (e) {
      if (e?.response?.status === 404) {
        setCurrent({ page, version: null, effective_date: null, content_html: null });
        setDraft("");
      } else {
        toast.error("Failed to load page");
      }
    }
  };

  const loadHistory = async () => {
    try {
      const r = await api.get(`/admin/legal-pages/${activePage}/history`);
      setHistory(r.data.versions || []);
      setShowHistory(true);
    } catch {
      toast.error("Failed to load history");
    }
  };

  const publish = async () => {
    if (draft.length < 200) {
      toast.error("Content must be at least 200 characters (this is a full policy, not a placeholder).");
      return;
    }
    if (!dirty) {
      toast.error("No changes detected. Edit the content before publishing.");
      return;
    }
    setPublishing(true);
    try {
      const body = { content_html: draft };
      if (changeNotes) body.change_notes = changeNotes;
      if (effectiveDate) body.effective_date = effectiveDate;
      if (explicitVersion) body.explicit_version = explicitVersion;
      const r = await api.post(`/admin/legal-pages/${activePage}/versions`, body);
      const publishedVer = r.data.published_version;
      toast.success(`Published ${publishedVer.version} for ${activePage}`);
      if (r.data.reaccept_gate_triggered) {
        toast.warning("Sender Agreement version bumped. All subscribers must re-accept before their next outreach send.", { duration: 8000 });
      }
      setChangeNotes("");
      setExplicitVersion("");
      setEffectiveDate("");
      await loadSummary();
      await loadPage(activePage);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  useEffect(() => { loadSummary(); loadPage("sender_agreement"); }, []);

  const activeSummary = summary?.pages?.find((p) => p.page === activePage);

  return (
    <div className="p-6 space-y-6" data-testid="admin-legal-pages">
      <style>{`
        .legal-quill-wrap .ql-toolbar { border:0; border-bottom:1px solid rgba(255,255,255,0.1); background:rgba(255,255,255,0.03); border-top-left-radius:2px; border-top-right-radius:2px; }
        .legal-quill-wrap .ql-container { border:0; font-size:14px; color:#E2E8F0; }
        .legal-quill-wrap .ql-editor { min-height:420px; line-height:1.6; }
        .legal-quill-wrap .ql-editor.ql-blank::before { color:#64748B; font-style:normal; }
        .legal-quill-wrap .ql-editor a { color:#34D399; }
        .legal-quill-wrap .ql-stroke { stroke:#94A3B8; }
        .legal-quill-wrap .ql-fill { fill:#94A3B8; }
        .legal-quill-wrap .ql-picker { color:#94A3B8; }
        .legal-quill-wrap .ql-toolbar button:hover .ql-stroke, .legal-quill-wrap .ql-toolbar button.ql-active .ql-stroke { stroke:#34D399; }
        .legal-quill-wrap .ql-toolbar button:hover .ql-fill, .legal-quill-wrap .ql-toolbar button.ql-active .ql-fill { fill:#34D399; }
        .legal-quill-wrap .ql-picker-options { background:#0A0D12; border-color:rgba(255,255,255,0.1); }
      `}</style>
      <div className="flex items-center gap-3">
        <FileText className="h-5 w-5 text-emerald-400" />
        <div>
          <h1 className="font-heading text-2xl">Legal Page Manager</h1>
          <p className="text-xs text-slate-500">Version-safe editing of the four public legal pages. Admin only.</p>
        </div>
      </div>

      {loading && <div className="text-slate-400 text-sm">Loading…</div>}

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {summary.pages.map((p) => (
            <button
              key={p.page}
              onClick={() => loadPage(p.page)}
              data-testid={`legal-page-tab-${p.page}`}
              className={`ae-card p-4 text-left transition-colors ${
                activePage === p.page ? "border-emerald-500/60 bg-emerald-500/8" : "hover:bg-white/5"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className={`h-4 w-4 ${p.reaccept_gate ? "text-amber-400" : "text-emerald-400"}`} />
                <span className="text-xs uppercase tracking-widest text-slate-500">{p.public_route}</span>
              </div>
              <div className="font-heading text-sm text-slate-200">{p.title}</div>
              <div className="text-xs text-slate-400 mt-2">
                Current: <span className="font-mono">{p.current_version || "(not published to DB yet)"}</span>
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                {p.total_versions} version{p.total_versions === 1 ? "" : "s"} in history
              </div>
              {p.reaccept_gate && (
                <div className="text-[10px] text-amber-400 mt-1">Re-accept gate active</div>
              )}
            </button>
          ))}
        </div>
      )}

      {summary && (
        <div className="ae-card p-3 text-[11px] text-slate-500 leading-relaxed" data-testid="legal-scope-note">
          {summary.note}
        </div>
      )}

      {current && (
        <div className="ae-card p-5 space-y-3" data-testid="legal-editor">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-lg">{activeSummary?.title}</h2>
              <div className="text-xs text-slate-500 mt-0.5">
                Current version: <span className="font-mono text-slate-300">{current.version || "(none published)"}</span>
                {current.effective_date ? ` · effective ${current.effective_date}` : ""}
              </div>
            </div>
            <button onClick={loadHistory} data-testid="show-history-btn"
                    className="btn-ghost text-xs inline-flex items-center gap-1">
              <History className="h-3.5 w-3.5" /> History ({activeSummary?.total_versions || 0})
            </button>
          </div>

          <div data-testid="legal-content-editor" className="legal-quill-wrap bg-black/40 border border-white/10 rounded-sm">
            <ReactQuill
              theme="snow"
              value={draft}
              onChange={(html, _delta, source) => { setDraft(html); if (source === "user") setDirty(true); }}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder="Write or paste the full body of this legal page. Formatting is saved as clean HTML."
            />
          </div>
          <div className="flex items-center gap-3 -mt-1">
            <button
              type="button"
              onClick={() => {
                if (current?.content_html) {
                  setDraft(current.content_html);
                  setDirty(false);
                  toast.success(`Loaded ${current.version} into editor`);
                } else {
                  toast.error("No published DB version to load yet. Publish the first version to seed history.");
                }
              }}
              disabled={!current?.content_html || !dirty}
              data-testid="load-current-version-btn"
              className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-slate-200 border border-white/15 rounded-sm px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-30"
            >
              <RefreshCw className="h-3 w-3" />
              Reload current published ({current?.version || "none"})
            </button>
            {dirty && (
              <span className="text-[10px] uppercase tracking-widest text-amber-400" data-testid="legal-draft-dirty">
                Unsaved changes
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Effective date (optional)</div>
              <input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)}
                      data-testid="effective-date"
                      className="w-full bg-white/3 border border-white/10 rounded-sm px-2 py-1.5 text-slate-200 text-sm" />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Explicit version (optional; auto-bumps otherwise)</div>
              <input value={explicitVersion} onChange={(e) => setExplicitVersion(e.target.value)}
                      placeholder={`e.g. ${_bumpHint(current.version)}`} data-testid="explicit-version"
                      className="w-full bg-white/3 border border-white/10 rounded-sm px-2 py-1.5 text-slate-200 text-sm font-mono" />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Change notes (audit)</div>
              <input value={changeNotes} onChange={(e) => setChangeNotes(e.target.value)}
                      placeholder="What changed and why"
                      data-testid="change-notes"
                      className="w-full bg-white/3 border border-white/10 rounded-sm px-2 py-1.5 text-slate-200 text-sm" />
            </label>
          </div>

          {activePage === "sender_agreement" && (
            <div className="flex items-start gap-2 text-xs text-amber-300 bg-amber-500/8 border border-amber-500/25 rounded-sm p-3">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                Publishing a new version of the <strong>Sender Agreement</strong> will invalidate every subscriber&apos;s prior acknowledgment. Their next outreach send will be refused until they re-accept the new version in Settings → Integrations. Privacy / Terms / Compliance edits do <strong>not</strong> trigger a re-accept.
              </div>
            </div>
          )}

          <button onClick={publish} disabled={publishing || !draft || !dirty}
                  data-testid="publish-version-btn"
                  className="px-4 py-2 text-xs uppercase tracking-widest rounded-sm border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25 inline-flex items-center gap-2">
            {publishing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
            {publishing ? "Publishing…" : "Publish new version"}
          </button>
        </div>
      )}

      {showHistory && (
        <div className="ae-card p-5 space-y-3" data-testid="legal-history">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg">Version history ,  {activeSummary?.title}</h3>
            <button onClick={() => setShowHistory(false)} className="btn-ghost text-xs">Hide</button>
          </div>
          {history.length === 0 && <div className="text-xs text-slate-500">No versions in DB yet.</div>}
          <div className="space-y-2">
            {history.map((v) => (
              <details key={v.vid} className="border border-white/10 rounded-sm bg-white/[0.02]">
                <summary className="p-3 cursor-pointer text-sm text-slate-300 hover:bg-white/5">
                  <span className="font-mono text-emerald-300">{v.version}</span>
                  {v.is_current && <span className="ml-2 text-[10px] uppercase tracking-widest text-emerald-400">Current</span>}
                  {v.effective_date && <span className="ml-3 text-xs text-slate-500">effective {v.effective_date}</span>}
                  <span className="ml-3 text-xs text-slate-500">by {v.created_by_email}</span>
                  <span className="ml-3 text-xs text-slate-500">at {fmtDateTime(v.created_at)}</span>
                </summary>
                <div className="p-3 space-y-2 text-xs">
                  {v.change_notes && <div className="text-slate-400"><strong>Notes:</strong> {v.change_notes}</div>}
                  {v.superseded_at && <div className="text-slate-500">superseded {fmtDateTime(v.superseded_at)} by {v.superseded_by}</div>}
                  <pre className="whitespace-pre-wrap font-mono text-[11px] text-slate-400 bg-black/40 border border-white/5 rounded-sm p-2 max-h-60 overflow-auto">
                    {(v.content_html || "").slice(0, 3000)}{(v.content_html || "").length > 3000 ? "\n\n… (truncated)" : ""}
                  </pre>
                </div>
              </details>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function _bumpHint(prev) {
  if (!prev) return "v1.0";
  const m = /^v?(\d+)\.(\d+)$/.exec(prev.trim());
  if (!m) return "v1.0";
  return `v${m[1]}.${parseInt(m[2], 10) + 1}`;
}
